import { API_BASE_URL } from '../constants/api';

const CSRF_COOKIE_NAME = process.env.REACT_APP_CSRF_COOKIE_NAME || 'lsq_csrf';
const CSRF_HEADER_NAME = 'X-CSRF-Token';
const AUTH_ME_ENDPOINT = '/api/v1/auth/me';
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
const inFlightSafeRequests = new Map();
const PAYLOAD_ENCRYPTED_HEADER = 'X-Payload-Encrypted';
const PAYLOAD_RESPONSE_REQUEST_HEADER = 'X-Response-Encrypted';
const PAYLOAD_ENCRYPTION_SCHEME = 'aes256gcm';
const PAYLOAD_ENCRYPTION_ALG = 'AES-256-GCM';
const resolvePayloadEncryptionKeyB64 = () => {
  const primary = String(process.env.REACT_APP_API_PAYLOAD_ENCRYPTION_KEY_B64 || '').trim();
  if (primary) return primary;
  // Backward compatibility for older frontend env naming.
  return String(process.env.REACT_APP_API_PAYLOAD_ENCRYPTION_KEY || '').trim();
};
const PAYLOAD_ENCRYPTION_KEY_B64 = resolvePayloadEncryptionKeyB64();
const textEncoder = new TextEncoder();

const ABSOLUTE_URL_PATTERN = /^https?:\/\//i;
const HIGH_PRIORITY_PATH_PATTERNS = [
  /^\/api\/playwright\/auth-capture\/(start|[^/]+\/(input|save))$/,
  /^\/api\/playwright\/auth-state\/[^/]+(\/refresh)?$/,
  /^\/api\/playwright\/(generate|regenerate|save|run)(\/(queue|batch|batch\/queue))?$/,
  /^\/api\/appium\/generate\/queue$/,
  /^\/api\/appium\/report(-batch)?$/,
  /^\/api\/feature-captures\/[^/]+\/generate-scripts\/queue$/,
  /^\/api\/feature-captures\/[^/]+\/run-script\/(queue|batch\/queue)$/,
  /^\/api\/pom\/auto-select\/queue$/,
  /^\/api\/test-cases\/save$/,
  /^\/api\/[^/]+\/save$/,
  /^\/api\/email-results\/[^/]+$/,
  /^\/api\/[^/]+\/execution-report-excel$/,
  /^\/api\/playwright\/github\/export$/,
  /^\/api\/upload-pdf-secure$/,
  /^\/api\/import-manual-test-cases-secure$/,
  /^\/api\/normalize-manual-test-cases-secure$/,
  /^\/api\/jobs\/[^/]+\/(cancel|retry|result)$/,
];

let csrfTokenCache = '';
let csrfTokenRequest = null;
let payloadCryptoKeyPromise = null;

const readCookie = (name) => {
  if (typeof document === 'undefined') return '';
  const prefix = `${name}=`;
  const cookie = document.cookie
    .split(';')
    .map((item) => item.trim())
    .find((item) => item.startsWith(prefix));
  if (!cookie) return '';
  return decodeURIComponent(cookie.slice(prefix.length));
};

const resolveRequestUrl = (input) => {
  const value = String(input || '');
  if (!value) return API_BASE_URL;
  if (ABSOLUTE_URL_PATTERN.test(value)) return value;
  return `${API_BASE_URL}${value}`;
};

const isCrossOriginRequest = (url) => {
  if (typeof window === 'undefined') return false;
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.origin !== window.location.origin;
  } catch {
    return false;
  }
};

const fetchCsrfTokenFromSession = async () => {
  if (csrfTokenRequest) return csrfTokenRequest;

  csrfTokenRequest = fetch(resolveRequestUrl(AUTH_ME_ENDPOINT), {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
    .then(async (response) => {
      if (!response.ok) return '';
      const payload = await response.json().catch(() => null);
      const token = String(payload?.csrf_token || '').trim();
      if (token) csrfTokenCache = token;
      return token;
    })
    .catch(() => '')
    .finally(() => {
      csrfTokenRequest = null;
    });

  return csrfTokenRequest;
};

const resolveCsrfToken = async (url) => {
  const cookieToken = readCookie(CSRF_COOKIE_NAME);
  if (cookieToken && !isCrossOriginRequest(url)) {
    csrfTokenCache = cookieToken;
    return cookieToken;
  }
  if (csrfTokenCache) return csrfTokenCache;
  return fetchCsrfTokenFromSession();
};

const bytesToBase64 = (bytes) => {
  let binary = '';
  const chunkSize = 0x8000;
  for (let index = 0; index < bytes.length; index += chunkSize) {
    const chunk = bytes.subarray(index, index + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
};

const base64ToBytes = (value) => {
  const binary = atob(String(value || ''));
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
};

const getUrlPath = (url) => {
  const fallbackOrigin = typeof window !== 'undefined' ? window.location.origin : API_BASE_URL;
  try {
    return new URL(url, fallbackOrigin).pathname;
  } catch (_err) {
    return '';
  }
};

const isHighPriorityPayloadPath = (url) => {
  const path = getUrlPath(url);
  return HIGH_PRIORITY_PATH_PATTERNS.some((pattern) => pattern.test(path));
};

const buildAad = (method, url) => {
  const path = getUrlPath(url) || '/';
  return textEncoder.encode(`${String(method || 'GET').toUpperCase()}:${path}`);
};

const getPayloadCryptoKey = async () => {
  if (payloadCryptoKeyPromise) {
    return payloadCryptoKeyPromise;
  }
  payloadCryptoKeyPromise = (async () => {
    if (!PAYLOAD_ENCRYPTION_KEY_B64) {
      throw new Error(
        'Missing payload encryption key. Set REACT_APP_API_PAYLOAD_ENCRYPTION_KEY_B64 and restart frontend.'
      );
    }
    const rawKey = base64ToBytes(PAYLOAD_ENCRYPTION_KEY_B64);
    if (rawKey.length !== 32) {
      throw new Error('REACT_APP_API_PAYLOAD_ENCRYPTION_KEY_B64 must decode to 32 bytes');
    }
    return crypto.subtle.importKey(
      'raw',
      rawKey,
      { name: 'AES-GCM' },
      false,
      ['encrypt', 'decrypt']
    );
  })();
  return payloadCryptoKeyPromise;
};

const encryptPayloadEnvelope = async (payload, method, url) => {
  const key = await getPayloadCryptoKey();
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const plaintext = textEncoder.encode(JSON.stringify(payload ?? {}));
  const ciphertext = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: nonce,
      additionalData: buildAad(method, url),
      tagLength: 128,
    },
    key,
    plaintext
  );
  return {
    scheme: PAYLOAD_ENCRYPTION_SCHEME,
    alg: PAYLOAD_ENCRYPTION_ALG,
    nonce: bytesToBase64(nonce),
    ciphertext: bytesToBase64(new Uint8Array(ciphertext)),
  };
};

const decryptPayloadEnvelope = async (envelope, method, url) => {
  const key = await getPayloadCryptoKey();
  const nonce = base64ToBytes(envelope?.nonce);
  const ciphertext = base64ToBytes(envelope?.ciphertext);
  const plaintext = await crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: nonce,
      additionalData: buildAad(method, url),
      tagLength: 128,
    },
    key,
    ciphertext
  );
  const decoded = new TextDecoder().decode(plaintext);
  return decoded ? JSON.parse(decoded) : {};
};

const rebuildJsonResponse = (response, payload) => {
  const responseHeaders = new Headers(response.headers);
  responseHeaders.set('Content-Type', 'application/json');
  responseHeaders.delete('Content-Length');
  responseHeaders.delete(PAYLOAD_ENCRYPTED_HEADER);
  return new Response(JSON.stringify(payload ?? {}), {
    status: response.status,
    statusText: response.statusText,
    headers: responseHeaders,
  });
};

const decryptJsonResponseIfNeeded = async (response, method, url) => {
  const encryptedResponse = (
    String(response.headers.get(PAYLOAD_ENCRYPTED_HEADER) || '').trim().toLowerCase()
    === PAYLOAD_ENCRYPTION_SCHEME
  );
  if (!encryptedResponse) {
    return response;
  }

  const responseEnvelope = await response.clone().json();
  const decryptedPayload = await decryptPayloadEnvelope(responseEnvelope, method, url);
  return rebuildJsonResponse(response, decryptedPayload);
};

export const apiFetch = async (input, options = {}) => {
  const method = String(options.method || 'GET').toUpperCase();
  const headers = new Headers(options.headers || {});
  const url = resolveRequestUrl(input);

  if (!SAFE_METHODS.has(method) && !headers.has(CSRF_HEADER_NAME)) {
    const csrfToken = await resolveCsrfToken(url);
    if (csrfToken) {
      headers.set(CSRF_HEADER_NAME, csrfToken);
    }
  }

  const requestOptions = {
    ...options,
    method,
    headers,
    credentials: 'include',
  };

  const wantsPayloadEncryption = (
    !SAFE_METHODS.has(method)
    && isHighPriorityPayloadPath(url)
    && String(headers.get('Content-Type') || '').toLowerCase().includes('application/json')
  );

  if (wantsPayloadEncryption) {
    const rawBody = requestOptions.body;
    if (rawBody == null) {
      requestOptions.body = JSON.stringify({});
    }

    let parsedBody = {};
    if (typeof requestOptions.body === 'string') {
      parsedBody = requestOptions.body ? JSON.parse(requestOptions.body) : {};
    } else if (typeof requestOptions.body === 'object') {
      parsedBody = requestOptions.body;
    } else {
      throw new Error('Encrypted API request body must be JSON');
    }

    const envelope = await encryptPayloadEnvelope(parsedBody, method, url);
    requestOptions.body = JSON.stringify(envelope);
    headers.set(PAYLOAD_ENCRYPTED_HEADER, PAYLOAD_ENCRYPTION_SCHEME);
    headers.set(PAYLOAD_RESPONSE_REQUEST_HEADER, PAYLOAD_ENCRYPTION_SCHEME);
    headers.set('Content-Type', 'application/json');
  }

  if (SAFE_METHODS.has(method)) {
    const dedupeKey = `${method}:${url}`;
    if (inFlightSafeRequests.has(dedupeKey)) {
      const response = await inFlightSafeRequests.get(dedupeKey);
      return decryptJsonResponseIfNeeded(response.clone(), method, url);
    }

    const requestPromise = fetch(url, requestOptions)
      .finally(() => {
        inFlightSafeRequests.delete(dedupeKey);
      });

    inFlightSafeRequests.set(dedupeKey, requestPromise);
    const response = await requestPromise;
    return decryptJsonResponseIfNeeded(response.clone(), method, url);
  }

  const response = await fetch(url, requestOptions);
  if (response.status === 401) {
    csrfTokenCache = '';
  }

  return decryptJsonResponseIfNeeded(response, method, url);
};
